import { useEffect, useState } from 'react'
import type { MissionDto } from '@/api/hooks'
import { isReturningMission } from '@/lib/inProgress'
import { CombatReplay } from '@/components/CombatReplay'
import { SeaBattleReport } from '@/components/SeaBattleReport'
import { GoldIcon, WoodIcon, RumIcon, StatIcon, BottleIcon, ScopeIcon, SkullIcon, PrestigeIcon } from '@/components/game/GameIcons'
import { RelaunchMissionButton } from '@/components/RelaunchMissionButton'
import { CancelMissionButton } from '@/components/CancelMissionButton'
import { ResourceTally } from '@/components/game/ui'
import { FleetCompositionChip } from '@/components/game/FleetCompositionChip'

const STATUS_COLOR: Record<MissionDto['status'], string> = {
  in_progress: 'text-sand-300',
  completed: 'text-green-300',
  cancelled: 'text-ocean-300',
  failed: 'text-rum-500',
}

export function MissionCard({ mission }: { mission: MissionDto }) {
  const isInProgress = mission.status === 'in_progress'
  // A cancelled mission whose fleet hasn't landed yet: ships stay `in_mission`
  // and sail home until `resolvesAt` (the return-arrival time) — show the same
  // countdown mechanism as an outbound mission, framed as a return trip.
  const isReturning = isReturningMission(mission)
  const remaining = useCountdown(isInProgress || isReturning ? mission.resolvesAt : null)
  const [showReplay, setShowReplay] = useState(false)
  const fleet = mission.fleet ?? []

  const combat = mission.payload.result?.combat
  const voyage = mission.payload.result?.voyage
  const bottle = mission.payload.result?.bottle
  const reputation = mission.payload.result?.reputation
  const loot = mission.payload.result?.loot
  const lostShips = mission.payload.result?.lostShips
  const cargo = mission.payload.cargo
  const crewWage = mission.payload.result?.crewWage ?? 0
  const ambush = mission.payload.result?.ambush
  const interception = mission.payload.result?.interception

  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg text-sand-300">{mission.typeLabel}</h3>
          {mission.shipName && (
            <p className="text-xs text-ocean-200">à bord de <em className="text-ocean-100">{mission.shipName}</em></p>
          )}
          {mission.destinationIslandName && (
            <p className="text-xs text-ocean-200">
              → <span className="text-sand-300">{mission.destinationIslandName}</span>
              {mission.destinationIslandTypeLabel && (
                <span className="text-ocean-300"> ({mission.destinationIslandTypeLabel})</span>
              )}
            </p>
          )}
          {fleet.length > 0 && (
            <div className="mt-1.5">
              <FleetCompositionChip fleet={fleet} idBase={`mission-${mission.id}`} />
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {isReturning ? (
            <span className="text-xs font-semibold text-ocean-300 inline-flex items-center gap-1">
              <span aria-hidden="true">↩</span> Retour au port
            </span>
          ) : (
            <span className={`text-xs font-semibold ${STATUS_COLOR[mission.status]}`}>
              {mission.statusLabel}
            </span>
          )}
          <RelaunchMissionButton mission={mission} />
          <CancelMissionButton mission={mission} />
        </div>
      </div>

      {isInProgress && (
        <div className="bg-ocean-900/55 rounded-lg p-3 border border-sand-500/10">
          <p className="text-[11px] text-ocean-300 uppercase tracking-wider">Temps restant</p>
          <p className="font-display text-2xl text-sand-100 tabular-nums">{remaining}</p>
        </div>
      )}

      {isReturning && (
        // Cancelled but still at sea — same countdown mechanism as an outbound
        // mission, framed as a homebound trip so it doesn't read as "stuck".
        <div className="bg-ocean-900/55 rounded-lg p-3 border border-ocean-400/20">
          <p className="text-[11px] text-ocean-300 uppercase tracking-wider flex items-center gap-1">
            <span aria-hidden="true">↩</span> Retour au port : temps restant
          </p>
          <p className="font-display text-2xl text-sand-100 tabular-nums">{remaining}</p>
        </div>
      )}

      {/* A landed RECALL (cancelled + no longer at sea) gets its report too: the
          hold it brought back is the whole point of the manoeuvre, and without
          this the player saw nothing at all once the fleet docked. */}
      {(mission.status === 'completed' || (mission.status === 'cancelled' && !isReturning)) && mission.payload.result && (
        <div className="bg-ocean-900/45 rounded-lg p-3 space-y-2">
          {/* Un bilan où pas une coque n'est rentrée (récif : fleetLost) se lit
              comme un échec au premier coup d'œil, sans avoir à déchiffrer
              l'absence de butin sous un titre couleur succès. */}
          <p className={`text-sm font-semibold ${mission.payload.result.fleetLost ? 'text-rum-300' : 'text-sand-100'}`}>
            {mission.payload.result.title}
          </p>
          <p className="text-xs text-ocean-200 italic">{mission.payload.result.message}</p>
          {mission.payload.result.outcome === 'scouting_repelled' && (
            // The éclaireur was spotted by the target's tour de guet and came home
            // empty-handed. Nudge the attacker toward a Longue-vue (Forge tech) so
            // recon out-depths the watchtower next time — or simply retry.
            <p className="text-xs text-sand-200 flex items-start gap-1.5 rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2">
              <ScopeIcon className="w-4 h-4 text-sand-300 shrink-0 mt-0.5" />
              <span>
                Ton éclaireur a été repéré et repoussé. Équipe une{' '}
                <strong className="text-sand-100">Longue-vue</strong> ou réessaie.
              </span>
            </p>
          )}
          {loot && (loot.gold > 0 || loot.wood > 0 || loot.rhum > 0) && (
            <p className="text-sm flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider text-ocean-300">Butin</span>
              {/* Raid loot is ALREADY net: the crew wage is taken on the gold-equivalent
                  total and evaporated server-side (loot = {gold,wood,rhum} net). Render it
                  as-is; the wage shows once on its own line below. Subtracting it here
                  double-counted it and rendered negative gold for resource raids.
                  Zero components are dropped (|| undefined) so a partial haul never shows a
                  stray "0" chip; an all-zero haul (e.g. a one-ship reef run that wiped) skips
                  the whole block via the guard above and only the loss block below shows. */}
              <ResourceTally gold={loot.gold || undefined} wood={loot.wood || undefined} rhum={loot.rhum || undefined} className="text-sand-200 font-semibold" />
            </p>
          )}
          {mission.payload.result.outcome === 'cancelled' && (() => {
            // Recall report: a demi-tour sells and buys nothing, so the unsold
            // cargo and the unspent gold come back down the gangway. Rendered
            // from `recovered` (what the entrepôt actually took, post-cap), never
            // from the launch payload. Nothing to show for a mission that only
            // ever spent its ration.
            const back = mission.payload.result.recovered
            if (!back || (!back.gold && !back.wood && !back.rhum)) return null
            return (
              <p className="text-sm flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wider text-ocean-300">Ramené à quai</span>
                <ResourceTally
                  gold={back.gold || undefined}
                  wood={back.wood || undefined}
                  rhum={back.rhum || undefined}
                  className="text-sand-200 font-semibold"
                />
              </p>
            )
          })()}
          {(mission.payload.result.outcome === 'smuggle_intercepted' || mission.payload.result.outcome === 'black_market_intercepted' || mission.payload.result.outcome === 'salvage_done') && lostShips && lostShips.length > 0 && (
            // Scuttled ships — interception (smuggle_intercepted / black_market_intercepted)
            // or the reef's hazard (salvage_done). Named so the player knows what went down.
            // Gated to these outcomes on purpose: raid results also carry lostShips but name them
            // inline in their narrative message — rendering this block for raids too would
            // print the lost ships twice.
            <p className="text-xs text-rum-300 flex items-start gap-1.5">
              <SkullIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong className="text-rum-200">Navire(s) perdu(s)</strong> : {lostShips.join(', ')}
              </span>
            </p>
          )}
          {mission.payload.result.outcome === 'black_market_intercepted' && (mission.payload.result.goldSpent ?? 0) > 0 && (
            // The marché noir pays GOLD upfront at launch; on interception that gold is
            // gone (nothing delivered). Surface it so the loss is legible — icons, never
            // the word "or" (mirror the trade/smuggle loss lines).
            <p className="text-xs text-rum-300 flex items-center gap-1">
              <span className="tabular-nums">−{mission.payload.result.goldSpent}</span>
              <GoldIcon className="w-3 h-3" /> or perdu (réglé à l'embarquement)
            </p>
          )}
          {mission.payload.result.outcome === 'trade_success' && cargo && (
            <p className="text-sm flex items-center gap-2 flex-wrap">
              <ResourceTally wood={cargo.wood || undefined} rhum={cargo.rum || undefined} className="text-ocean-200" />
              <span className="text-ocean-400" aria-hidden="true">→</span>
              <ResourceTally gold={(mission.payload.result.goldEarned ?? 0) - crewWage} className="text-sand-200 font-semibold" />
            </p>
          )}
          {(mission.payload.result.outcome === 'trade_buy' || mission.payload.result.outcome === 'black_market_success') && (() => {
            const r = mission.payload.result
            // The buy-side report, shared by legal commerce (trade_buy) and the
            // marché noir (black_market_success): both spend gold upfront → a
            // resource lands home. Identical payload keys (goldSpent, boughtResource,
            // storedAmount, lostAmount), so the block is the SAME.
            // boughtResource is a ResourceType value ('wood' | 'rum'); the buy
            // panel now reaches rhum distilleries too, so the report icon follows
            // the bought good rather than being hard-wired to wood. Map it to the
            // matching ResourceTally prop ('rum' → rhum) so amounts render as icons.
            const isRum = r.boughtResource === 'rum'
            const ResourceGlyph = isRum ? RumIcon : WoodIcon
            const stored = r.storedAmount ?? r.boughtAmount
            const lost = r.lostAmount ?? 0
            return (
              <>
                <p className="text-sm flex items-center gap-2 flex-wrap">
                  <ResourceTally gold={r.goldSpent} className="text-ocean-200" />
                  <span className="text-ocean-400" aria-hidden="true">→</span>
                  <ResourceTally
                    wood={isRum ? undefined : stored}
                    rhum={isRum ? stored : undefined}
                    className="text-sand-200 font-semibold"
                  />
                </p>
                {lost > 0 && (
                  <p className="text-[11px] text-rum-300 flex items-center gap-1">
                    <span className="tabular-nums">−{lost}</span>
                    <ResourceGlyph className="w-3 h-3" /> perdus en mer (entrepôt plein au retour)
                  </p>
                )}
              </>
            )
          })()}
          {mission.payload.result.outcome === 'discovery' && (
            <p className="text-sm flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider text-ocean-300">Récompense</span>
              <ResourceTally gold={(mission.payload.result.goldEarned ?? 0) - crewWage} className="text-sand-200 font-semibold" />
            </p>
          )}
          {mission.payload.result.outcome === 'smuggle_success' && (
            // Contrebande is a sale (cargo fenced → gold), structurally identical to
            // trade_success: show the goods spent → the gold banked NET of the crew share
            // (goldEarned is GROSS; the wage shows once on its own line below). Falls back
            // to a plain "Butin → gold" when the cargo isn't on the payload (older missions).
            <p className="text-sm flex items-center gap-2 flex-wrap">
              {cargo && (cargo.wood || cargo.rum) ? (
                <>
                  <ResourceTally wood={cargo.wood || undefined} rhum={cargo.rum || undefined} className="text-ocean-200" />
                  <span className="text-ocean-400" aria-hidden="true">→</span>
                </>
              ) : (
                <span className="text-[11px] uppercase tracking-wider text-ocean-300">Butin</span>
              )}
              <ResourceTally gold={(mission.payload.result.goldEarned ?? 0) - crewWage} className="text-sand-200 font-semibold" />
            </p>
          )}
          {(() => {
            // Loot/sale clamped by a full warehouse at return — the overflow used
            // to vanish silently; every crediting mission now reports it (the buy
            // runs keep their own storedAmount/lostAmount line above).
            const lost = mission.payload.result.overflowLost
            if (!lost || (!lost.gold && !lost.wood && !lost.rhum)) return null
            return (
              <p className="text-[11px] text-rum-300 flex items-center gap-1.5 flex-wrap">
                <span aria-hidden="true">−</span>
                <ResourceTally gold={lost.gold || undefined} wood={lost.wood || undefined} rhum={lost.rhum || undefined} className="text-rum-300" />
                <span>perdus en mer (entrepôt plein au retour)</span>
              </p>
            )
          })()}
          {crewWage > 0 && (
            <p className="text-[11px] text-ocean-300 flex items-center gap-1">
              <span aria-hidden="true">⚓</span> Part de l'équipage{' '}
              <span className="tabular-nums">−{crewWage.toLocaleString('fr-FR')}</span>
              <GoldIcon className="w-3 h-3" />
            </p>
          )}
          {reputation && (
            <p className="text-xs text-ocean-200 flex items-center gap-1.5">
              <span className="text-sm leading-none" aria-hidden="true" title={reputation.factionLabel}>{reputation.pavillon}</span>
              <span><strong className="text-sand-200">Réputation</strong> ({reputationChanges(reputation)})</span>
            </p>
          )}
          {voyage && (
            <div className="rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2">
              <p className="text-[10px] uppercase tracking-wider text-ocean-300 flex items-center gap-1.5">
                <span aria-hidden="true">🪶</span> Journal de bord ({voyage.label})
              </p>
              <p className="text-xs text-ocean-200 italic mt-1">{voyage.message}</p>
              {voyage.amount > 0 && (voyage.effect === 'gold_find' ? (
                <p className="text-[11px] text-green-300 mt-1 flex items-center gap-1">
                  <span className="tabular-nums">+{voyage.amount}</span>
                  <GoldIcon className="w-3 h-3" />
                  <span>(aubaine)</span>
                </p>
              ) : (
                <p className="text-[11px] text-rum-300 mt-1 flex items-center gap-1">
                  <span className="tabular-nums">−{voyage.amount}</span>
                  <GoldIcon className="w-3 h-3" />
                  <span>{voyage.effect === 'gold_cost' ? '(frais)' : '(cargaison perdue)'}</span>
                </p>
              ))}
            </div>
          )}
          {bottle && (
            <div className="rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2">
              <p className="text-[10px] uppercase tracking-wider text-ocean-300 flex items-center gap-1.5">
                <BottleIcon className="w-3.5 h-3.5 text-sand-300" /> Journal de bord ({bottle.label})
              </p>
              <p className="text-xs text-ocean-200 italic mt-1">{bottle.message}</p>
              <p className="text-[11px] text-sand-200 mt-1">
                Nouvelle quête : <span className="text-sand-100">« {bottle.questTitle} »</span>
              </p>
            </div>
          )}
          <KrakenHuntReport result={mission.payload.result} />
          {combat && combat.events.length > 0 && (
            <button
              onClick={() => setShowReplay(true)}
              className="text-xs px-3 py-1.5 rounded-md bg-rum-500/80 text-ocean-50 font-semibold hover:bg-rum-500 transition-colors inline-flex items-center gap-1.5"
            >
              <StatIcon name="abordage" className="w-3.5 h-3.5" />
              Revivre le combat
            </button>
          )}
          <SeaBattleReport ambush={ambush} interception={interception} />
        </div>
      )}

      <p className="text-[10px] text-ocean-300">
        {mission.status === 'completed' && mission.completedAt
          ? `Terminée le ${new Date(mission.completedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
          : `Démarrée le ${new Date(mission.startedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`}
      </p>

      {showReplay && combat && (
        <CombatReplay combat={combat} onClose={() => setShowReplay(false)} />
      )}
    </div>
  )
}

function useCountdown(targetIso: string | null): string {
  const compute = () => {
    if (!targetIso) return '—'
    const ms = new Date(targetIso).getTime() - Date.now()
    if (ms <= 0) return 'Résolution…'
    const totalSec = Math.floor(ms / 1000)
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`
    return `${pad(m)}:${pad(s)}`
  }

  const [text, setText] = useState(compute)

  useEffect(() => {
    if (!targetIso) return
    const id = window.setInterval(() => setText(compute()), 1000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIso])

  return text
}

function pad(n: number): string {
  return n.toString().padStart(2, '0')
}

/** "Hors-la-loi +2 · Guerrier +1" — names each axis by the direction it moved. */
function reputationChanges(r: { loyaltyDelta: number; methodDelta: number }): string {
  const parts: string[] = []
  if (r.loyaltyDelta !== 0) parts.push(`${r.loyaltyDelta > 0 ? 'Légal' : 'Hors-la-loi'} +${Math.abs(r.loyaltyDelta)}`)
  if (r.methodDelta !== 0) parts.push(`${r.methodDelta > 0 ? 'Guerrier' : 'Marchand'} +${Math.abs(r.methodDelta)}`)
  return parts.join(' · ')
}

/**
 * Le bilan d'un engagement contre la bête de fin de saison (fiche 32).
 *
 * Deux informations priment sur toutes les autres, et dans cet ordre : combien
 * de coques ne sont pas rentrées, et le fait que les dégâts infligés NE SE
 * REPORTENT PAS. Sans cette seconde phrase, un capitaine lirait « 97 % » et
 * croirait avoir entamé un travail ; il retrouvera la bête intacte.
 *
 * Le libellé de la bête vient de `archetypeLabel`, jamais du rapport de bataille :
 * `FleetBattleResult::fleetSummary` réduit tout combattant non-navire au mot
 * « fort », qui serait faux ici.
 */
function KrakenHuntReport({ result }: { result: NonNullable<MissionDto['payload']['result']> }) {
  // Défensif : `outcome` est déclaré obligatoire par le DTO, mais des rapports
  // partiels existent en base (missions anciennes) et un crash ici emporterait
  // toute la carte de mission.
  const outcome = result?.outcome ?? ''
  if (!outcome.startsWith('hunt_') || outcome === 'hunt_invalid') return null

  const max = result.coreMaxHp ?? 0
  const left = result.coreHpAtEnd ?? 0
  const dealtShare = max > 0 ? Math.min(100, Math.round(((max - left) / max) * 100)) : 0
  const lost = result.lostShips?.length ?? 0
  const trophy = result.trophy ?? null

  return (
    <div className="rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2 space-y-2">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-ocean-300">
          {result.archetypeLabel ?? 'La bête'} · descendu à
        </p>
        <p className="font-display text-lg text-sand-100 tabular-nums">
          {left.toLocaleString('fr-FR')} <span className="text-xs text-ocean-300">/ {max.toLocaleString('fr-FR')} PV</span>
        </p>
        <div className="mt-1 h-1.5 rounded-full bg-ocean-900 overflow-hidden" role="presentation">
          <div className="h-full bg-gradient-to-r from-rum-700 to-red-500" style={{ width: `${dealtShare}%` }} />
        </div>
        {!result.felled && (
          <p className="mt-1 text-[11px] text-ocean-300">
            Sans effet sur la suite : la bête repart intacte à chaque tentative.
          </p>
        )}
      </div>

      {result.fleetAnnihilated && (
        <p className="text-xs text-red-300">
          <strong className="font-semibold">{lost} coque(s) englouties</strong>, la flotte entière. Rien n'est rentré.
        </p>
      )}
      {result.felled && (
        <p className="text-xs text-sand-200">
          Tu en es venu à bout. {lost > 0 ? `${lost} coque(s) laissée(s) au fond.` : 'Aucune perte.'}
        </p>
      )}
      {trophy && (
        <p className="text-xs text-sand-200 inline-flex items-center gap-1.5 flex-wrap">
          <PrestigeIcon className="w-3.5 h-3.5 text-sand-300" />
          Titre gagné : <span className="text-sand-100">« {trophy.titleLabel} »</span>
          <span className="tabular-nums">+{trophy.prestige} prestige</span>
        </p>
      )}
      {typeof result.attemptsLeft === 'number' && !result.felled && (
        <p className="text-[11px] text-ocean-300">
          {result.escaped
            ? 'Budget épuisé : elle a replongé pour de bon, plus rien à traquer cette saison.'
            : `${result.attemptsLeft} tentative(s) restante(s)${result.relocated ? ', et elle a refait surface ailleurs' : ", et elle reste là où tu l'as laissée"}.`}
        </p>
      )}
    </div>
  )
}
